import { format } from "date-fns";
import * as React from "react";
import { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { ThunkDispatch } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementsById } from "core-integration/src/store/elements/types";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getGroupedFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import {
  IFixtureStat,
  IFixtureStats,
} from "core-integration/src/store/fixtures/types";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { dateLocales } from "../i18n";
import Logo from "../img/logos/es-logo.svg?react";
import Button from "./Button";
import Fixture from "./Fixture";
import { Pager, PagerButton, PagerButtonNext } from "./Pager";
import { VisuallyHidden } from "./Utils";
import { ControlArrowLeft, ControlArrowRight } from "./icons/Arrows";

const Deadline = styled.h2`
  margin: ${({ theme }) => theme.space[3]};
  font-family: ${({ theme }) => theme.fonts.baseSemiBold};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  text-align: center;
`;

const Heading = styled.h3`
  margin: ${({ theme }) => theme.space[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2.4rem;
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
`;

const Note = styled.p`
  margin: 0;
  text-align: center;
`;

const FixtureDay = styled.h4`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  color: ${({ theme }) => theme.colors.blackLighter};
  text-align: center;
  font-weight: 100;
`;

const FixtureTime = styled.time`
  font-weight: 100;
`;

const FixtureList = styled.ul`
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureItem = styled.li`
  margin-bottom: ${({ theme }) => theme.space[2]};
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureButton = styled.button<IStyledFixtureStatsProps>`
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
`;

const FixtureStatsWrap = styled.div<IStyledFixtureStatsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  padding: ${({ theme }) => `${theme.space[2]} 5%`};
  border-top: 1px solid ${({ theme }) => theme.colors.greyDarker1};
  background: ${({ theme }) => theme.colors.bgWhiteOpacity50};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    padding-right: 15%;
    padding-left: 15%;
  }
`;

const FixtureStatsOuterList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FixtureStat = styled.li`
  margin-bottom: ${({ theme }) => theme.space[4]};
  border: 1px solid ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radii[1]};
  background: ${({ theme }) => theme.colors.bgWhiteOpacity95};
`;

const FixtureStatHeading = styled.h5`
  margin: 0;
  padding: 0.3rem ${({ theme }) => theme.space[2]};
  border-top-left-radius: ${({ theme }) => theme.radii[0]};
  border-top-right-radius: ${({ theme }) => theme.radii[0]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[0]};
  text-align: center;
`;

const FixtureStatBody = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.space[2]};
  padding-bottom: ${({ theme }) => theme.space[2]};
`;

const FixtureStatList = styled.ul`
  flex: 1;
  padding-right: ${({ theme }) => theme.space[2]};
  padding-left: ${({ theme }) => theme.space[2]};

  &:first-child {
    border-right: 1px dotted black;
    text-align: right;
  }
`;

const FixtureStatItem = styled.li`
  padding-top: ${({ theme }) => theme.space[1]};
  padding-bottom: ${({ theme }) => theme.space[1]};
  border-bottom: 1px solid #e2e2e2;
  font-size: ${({ theme }) => theme.fontSizes[2]};
  list-style: none;

  &:last-child {
    border-bottom: 0;
  }
`;

const ElementButton = styled.button`
  background: transparent;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[2]};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

interface IFixtureStatsForTeamProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStat[];
}

const FixtureStatsForTeam: React.FC<IFixtureStatsForTeamProps> = ({
  elementsById,
  showElementDialog,
  stats,
}) => (
  <FixtureStatList>
    {stats
      .filter((fs) => elementsById[fs.element])
      .map((fs) => (
        <FixtureStatItem key={fs.element}>
          <ElementButton
            onClick={(_: React.MouseEvent<HTMLButtonElement>) =>
              showElementDialog(fs.element)
            }
          >
            {elementsById[fs.element].web_name}
          </ElementButton>{" "}
          ({fs.value})
        </FixtureStatItem>
      ))}
  </FixtureStatList>
);

interface IFixtureStatsProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStats[];
  statsByIdentifier: Record<string, IElementStat>;
}

interface IStyledFixtureStatsProps {
  isOpen?: boolean;
}

type FixtureStatProps = IFixtureStatsProps & IStyledFixtureStatsProps;

const FixtureStats: React.FC<FixtureStatProps> = ({
  elementsById,
  isOpen = false,
  showElementDialog,
  stats,
  statsByIdentifier,
}) => {
  // Our render stats should contain only stats with data and a maximum
  // of 5 per team of BPS
  const renderStats = stats
    .filter((stat) => stat.h.length || stat.a.length)
    .map((stat) =>
      stat.identifier === "bps"
        ? {
            identifier: stat.identifier,
            h: stat.h.slice(0, 5),
            a: stat.a.slice(0, 5),
          }
        : stat,
    );
  return (
    <FixtureStatsWrap isOpen={isOpen}>
      <FixtureStatsOuterList>
        {renderStats.map((stat) => (
          <FixtureStat key={stat.identifier}>
            <FixtureStatHeading>
              {statsByIdentifier[stat.identifier].label}
            </FixtureStatHeading>
            <FixtureStatBody>
              <FixtureStatsForTeam
                stats={stat.h}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
              <FixtureStatsForTeam
                stats={stat.a}
                elementsById={elementsById}
                showElementDialog={showElementDialog}
              />
            </FixtureStatBody>
          </FixtureStat>
        ))}
      </FixtureStatsOuterList>
    </FixtureStatsWrap>
  );
};

interface IOwnProps {
  eventId?: number;
  useLinks?: boolean;
}

type Props = IOwnProps & WithTranslation;

const Fixtures = ({
  eventId: initialEventId,
  i18n,
  t,
  useLinks = false,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const currentEvent = useSelector(getCurrentEvent);
  const elementsById = useSelector(getElementsById);
  const eventsById = useSelector(getEventsById);
  const groupedFixturesByEvent = useSelector(getGroupedFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);
  const statsByIdentifier = useSelector(getElementStatsByIdentifier);
  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});

  const [eventId, setEventId] = useState(() => {
    if (initialEventId) {
      return initialEventId;
    } else if (nextEvent) {
      return nextEvent.id;
    } else if (currentEvent) {
      return currentEvent.id;
    }
    return 0;
  });

  useEffect(() => {
    dispatch(fetchFixtures(eventId));
  }, [dispatch, eventId]);

  const changeEvent = (newEventId: number) => {
    setEventId(newEventId);
    dispatch(fetchFixtures(newEventId));
  };

  const toggle = (fixtureId: number) => {
    setIsOpen((prevState) => ({
      ...prevState,
      [fixtureId]: !prevState[fixtureId],
    }));
  };

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const event = eventsById[eventId];
  if (!event) {
    return null;
  }

  const eventFixtureGroups = groupedFixturesByEvent[eventId] || [];
  return (
    <section>
      <header>
        <Heading>
          <Logo />
          <VisuallyHidden>{t("fixtures.title", "Fixtures")}</VisuallyHidden>
        </Heading>
        <Pager>
          {eventsById[eventId - 1] && (
            <PagerButton>
              {useLinks ? (
                <Button
                  width={1}
                  to={`/fixtures/${eventId - 1}`}
                  onClick={() => changeEvent(eventId - 1)}
                  variant="secondary"
                  startIcon={<ControlArrowLeft />}
                >
                  {t("fixtures.previous", "Previous")}
                </Button>
              ) : (
                <Button
                  width={1}
                  onClick={() => changeEvent(eventId - 1)}
                  variant="secondary"
                  startIcon={<ControlArrowLeft />}
                >
                  {t("fixtures.previous", "Previous")}
                </Button>
              )}
            </PagerButton>
          )}
          {eventsById[eventId + 1] && (
            <PagerButtonNext>
              {useLinks ? (
                <Button
                  width={1}
                  to={`/fixtures/${eventId + 1}`}
                  onClick={() => changeEvent(eventId + 1)}
                  variant="secondary"
                  endIcon={<ControlArrowRight />}
                >
                  {t("fixtures.next", "Next")}
                </Button>
              ) : (
                <Button
                  width={1}
                  onClick={() => changeEvent(eventId + 1)}
                  disabled={!eventsById[eventId + 1]}
                  variant="primary"
                  endIcon={<ControlArrowRight />}
                >
                  {t("fixtures.next", "Next")}
                </Button>
              )}
            </PagerButtonNext>
          )}
        </Pager>
        <Deadline>
          {event.name} -{" "}
          <time dateTime={formatRawAsISO(event.deadline_time)}>
            {formatRawAsLocalI18n(
              event.deadline_time,
              dateLocales[i18n.language],
            )}
          </time>
        </Deadline>
      </header>
      <Note>
        {t("fixtures.localTimes", "All times are shown in your local time")}
      </Note>
      {eventFixtureGroups.map((group) => (
        <Box m={3} key={group.date.toISOString()}>
          <FixtureDay>
            <FixtureTime dateTime={group.date.toISOString()}>
              {format(group.date, "EEEE d MMMM yyyy")}
            </FixtureTime>
          </FixtureDay>
          <FixtureList>
            {group.fixtures.map((f) => (
              <React.Fragment key={f.id}>
                {f.started && f.team_h_score !== null ? (
                  <FixtureItem>
                    <FixtureButton
                      onClick={() => toggle(f.id)}
                      isOpen={isOpen[f.id]}
                    >
                      <Fixture fixture={f} />
                    </FixtureButton>
                    <FixtureStats
                      stats={f.stats}
                      elementsById={elementsById}
                      showElementDialog={showElementDialog}
                      statsByIdentifier={statsByIdentifier}
                      isOpen={isOpen[f.id]}
                    />
                  </FixtureItem>
                ) : (
                  <FixtureItem>
                    <Fixture fixture={f} />
                  </FixtureItem>
                )}
              </React.Fragment>
            ))}
          </FixtureList>
        </Box>
      ))}
    </section>
  );
};

export default withTranslation()(Fixtures);
